<template>
  <div id="account">
    <div id="content">
      <h1>{{ i18n.app.components.account.my_account }}</h1>

      <h3 v-if="client && client.company">{{ client.company.name }}</h3>
      <Form ref="form" :onSubmit="onSubmit" :onSuccess="onSuccess">
        <HiddenInput :model="model" name="user_id" />

        <div class="row">
          <div class="col-md-6">
            <fieldset class="bg-white rounded p-3 mb-3">
              <div class="form-group row">
                <div class="col-sm-12">
                  <h3>{{ i18n.app.components.account.language_settings }}</h3>
                </div>
              </div>

              <Select
                :label="i18n.app.common.fields.language"
                :model="model"
                name="language_id"
                :options="languages"
              />

              <Select
                :label="i18n.app.common.fields.country"
                :model="model"
                name="country_id"
                :options="countries"
              />
            </fieldset>

            <fieldset class="bg-white rounded p-3 mb-3" v-if="hasFrontendAccess">
              <div class="form-group row">
                <div class="col-sm-12">
                  <h3>{{ i18n.app.components.account.search }}</h3>
                </div>
              </div>

              <Select
                :label="i18n.app.components.account.standard_country"
                :textWhenNull="i18n.database.table.countries.all"
                :model="model"
                name="search_country_id"
                :options="countries"
              />

              <Select
                :label="i18n.app.components.account.standard_profile"
                :model="model"
                name="contract_template_id"
                :allowsNull="true"
                :textWhenNull="i18n.database.table.contract_templates.texts.no_profile"
                :options="contractTemplateOptions"
              />
            </fieldset>

            <fieldset v-if="hasApiRole" class="bg-white rounded p-3 mb-3">
              <ApiTokenListComponent :items="api_tokens" />
            </fieldset>
          </div>
          <div class="col-md-6">
            <fieldset class="bg-white rounded p-3 mb-3">
              <div class="form-group row mb-0 mt-2">
                <label
                  for="inputEmail3"
                  class="col-sm-2 col-form-label"
                >{{ i18n.app.components.account.view }}</label>
                <div class="col-sm-10">
                  <ul class="theme-switcher">
                    <li
                      class="nav-item"
                      :class="model.appearance == Appearance.LIGHT ? 'active' : ''"
                    >
                      <a
                        @click="onAppearanceChange(Appearance.LIGHT)"
                        class="nav-link light"
                        :class="model.appearance == Appearance.LIGHT ? 'active' : ''"
                        href="#"
                        data-theme="light.css"
                      >
                        <span>
                          <i class="cs-light"></i>
                        </span>
                        {{ i18n.app.components.account.light }}
                      </a>
                    </li>
                    <li
                      class="nav-item"
                      :class="model.appearance == Appearance.DARK ? 'active' : ''"
                    >
                      <a
                        @click="onAppearanceChange(Appearance.DARK)"
                        class="nav-link dark"
                        :class="model.appearance == Appearance.DARK ? 'active' : ''"
                        href="#"
                        data-theme="dark.css"
                      >
                        <span>
                          <i class="cs-dark"></i>
                        </span>
                        {{ i18n.app.components.account.dark }}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </fieldset>

            <fieldset class="bg-white rounded p-3 mb-3" v-if="hasFrontendAccess">
              <div class="form-group row">
                <div class="col-sm-12">
                  <h3>{{ i18n.app.common.rent_contracts }}</h3>
                  <label>{{ i18n.app.components.account.rent_contracts_text }}</label>
                </div>
              </div>

              <!-- <FormGroup name="contract_columns"> -->
              <div class="form-group row">
                <div
                  class="col-sm-6"
                  v-for="(contract_column, index) in contract_columns"
                  v-bind:key="contract_column.value"
                >
                  <Checkbox
                    :model="model.contract_columns_dict"
                    :id="'contract_columns-' + index"
                    :name="contract_column.value"
                    :label="contract_column.text"
                  />
                </div>
              </div>
              <!-- </FormGroup> -->

              <Select
                :label="i18n.app.components.account.standard_license_plate_countrycode"
                textWhenNull="Geen"
                :model="model"
                name="vehicle_country_id"
                :options="license_plate_countries"
              />
            </fieldset>

            <div class="mt-3 text-right">
              <button
                @click.prevent="onSubmitPress"
                class="btn btn-secondary"
                :class="loading && 'load-spinner'"
              >
                <span>{{ i18n.app.common.save }}</span>
              </button>
            </div>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script>
import Select from "@/components/common/form/Select";
import Checkbox from "@/components/common/form/Checkbox";
import HiddenInput from "@/components/common/form/HiddenInput";
import FormGroup from "@/components/common/form/FormGroup";
import Form from "@/components/common/form/Form";
import ApiTokenListComponent from "./partials/ApiTokenListComponent";

import UserPreference, { Appearance } from "@/models/UserPreference";
import UserService from "@/services/UserService";
import * as pickBy from "lodash/pickBy";
import * as keys from "lodash/keys";

import isTrue from "@/helpers/isTrue";

import { mapActions, mapGetters } from "vuex";

const userService = new UserService();

export default {
  name: "account",
  data() {
    return {
      loading: false,
      Appearance: Appearance,
      countries: this.$route.params.countries,
      languages: this.$route.params.languages,
      contract_templates: this.contractTemplateOptions,
      contract_columns: this.$route.params.contract_columns,
      license_plate_countries: this.$route.params.license_plate_countries,
      model: new UserPreference(this.$route.params.preference),
      api_tokens: this.$route.params.api_tokens,
    };
  },
  methods: {
    onSubmitPress() {
      this.$refs.form.submit(this.model);
    },
    onSubmit(model) {
      this.loading = true;
      model.contract_columns = keys(
        pickBy(this.model.contract_columns_dict, isTrue)
      ); // Transforms obj {'key1': true, 'key2': false} to ['key1']
      model.contract_columns = model.contract_columns.filter(
        item =>
          this.contract_columns.findIndex(_item => _item.value == item) > -1
      );
      model.contract_columns.sort((a, b) => {
        return (
          this.contract_columns.findIndex(item => item.value == a) -
          this.contract_columns.findIndex(item => item.value == b)
        );
      });

      model.appearance = this.model.appearance;
      return userService.updatePreference(model);
    },
    onSubmitFail() {
      this.loading = true;
    },
    onSuccess(preference) {
      this.loading = false;

      this._updateUserPreference(preference);

      this.$router.go(-1);
    },
    onAppearanceChange(appearance) {
      this.model.appearance = appearance;

      document.body.className = appearance.toLowerCase();

      this._updateUserPreference({
        appearance: appearance
      });

      userService.updatePreference({
        user_id: this.model.user_id,
        appearance: appearance
      });
    },
    ...mapActions({
      _updateUserPreference: "updateUserPreference" // map `this.add()` to `this.$store.dispatch('increment')`
    }),
  },
  computed: {
    ...mapGetters({
      contractTemplateOptions: "contractTemplateOptions",
      client: "client",
      user: "user",
      hasFrontendAccess: "hasFrontendAccess",
      hasReadonlyRole: "hasReadonlyRole",
      hasAdminReadonlyRole: "hasAdminReadonlyRole",
      hasApiRole: "hasApiRole",
    })
  },
  components: {
    Select,
    Checkbox,
    Form,
    HiddenInput,
    FormGroup,
    ApiTokenListComponent
  }
};
</script>
